/**
 * Configuration — Keywords workflow
 * Validation, non-overlapping Active-rule checks, save/confirm, activate / make Inactive.
 * No Priority Number. No Delete.
 */

import { getActorDisplayName } from './teamConfig';
import {
  CONFIGURATION_STATUS,
  KEYWORD_USE,
  MESSAGES,
  RULE_SCOPE,
  buildRuleBehaviourSummary,
  findDuplicateRuleName,
  formatScopeSelectionDisplay,
  nextKeywordRuleId,
  normalizeKeywordRule,
} from './keywordsConfig';
import { getTradeSubcategories } from './tradeCategoryWorkflow';
import { emitSnapshotHistoryEntry } from './changeHistoryEmitters';

const emitKeywordCentralHistory = (rule, localEntry) => {
  if (!rule || !localEntry) return;
  emitSnapshotHistoryEntry({
    localEntry,
    module: 'Configuration — Keywords',
    sourceModuleRef: 'keywords_management',
    changedItem: rule.ruleName || rule.id,
    itemId: rule.id,
  });
};

const nowIso = () => new Date().toISOString();

export function buildKeywordConfigActor(auth) {
  if (!auth) return { name: 'System', accountId: '—' };
  return { name: getActorDisplayName(auth), accountId: '—' };
}

function asIdSet(ids = []) {
  return new Set((ids || []).filter(Boolean));
}

function intersects(a, b) {
  for (const id of a) {
    if (b.has(id)) return true;
  }
  return false;
}

function subcategoryParentMap(sources = []) {
  const map = new Map();
  (sources || []).forEach((category) => {
    getTradeSubcategories(category).forEach((sub) => {
      map.set(sub.id, category.id);
    });
  });
  return map;
}

/**
 * Active overlap exists when two rules in the same Application Area could apply
 * to the same Keyword. No priority / specificity override.
 */
export function scopesOverlap(ruleA, ruleB, sources = []) {
  if (!ruleA || !ruleB) return false;
  if (ruleA.applicationArea !== ruleB.applicationArea) return false;

  const scopeA = ruleA.ruleScope;
  const scopeB = ruleB.ruleScope;

  if (scopeA === RULE_SCOPE.ALL_KEYWORDS || scopeB === RULE_SCOPE.ALL_KEYWORDS) {
    return true;
  }

  if (
    scopeA === RULE_SCOPE.SELECTED_TRADE_CATEGORIES &&
    scopeB === RULE_SCOPE.SELECTED_TRADE_CATEGORIES
  ) {
    return intersects(asIdSet(ruleA.tradeCategoryIds), asIdSet(ruleB.tradeCategoryIds));
  }

  if (
    scopeA === RULE_SCOPE.SELECTED_SUBCATEGORIES &&
    scopeB === RULE_SCOPE.SELECTED_SUBCATEGORIES
  ) {
    return intersects(asIdSet(ruleA.subcategoryIds), asIdSet(ruleB.subcategoryIds));
  }

  const parentMap = subcategoryParentMap(sources);
  if (
    scopeA === RULE_SCOPE.SELECTED_TRADE_CATEGORIES &&
    scopeB === RULE_SCOPE.SELECTED_SUBCATEGORIES
  ) {
    const parents = asIdSet(ruleA.tradeCategoryIds);
    const subParents = asIdSet(
      (ruleB.subcategoryIds || []).map((id) => parentMap.get(id)).filter(Boolean),
    );
    return intersects(parents, subParents);
  }
  if (
    scopeB === RULE_SCOPE.SELECTED_TRADE_CATEGORIES &&
    scopeA === RULE_SCOPE.SELECTED_SUBCATEGORIES
  ) {
    const parents = asIdSet(ruleB.tradeCategoryIds);
    const subParents = asIdSet(
      (ruleA.subcategoryIds || []).map((id) => parentMap.get(id)).filter(Boolean),
    );
    return intersects(parents, subParents);
  }

  return false;
}

export function findActiveOverlap(form, existingRules = [], sources = [], excludeId) {
  const candidate = normalizeKeywordRule(form, sources);
  if (candidate.configurationStatus !== CONFIGURATION_STATUS.ACTIVE) return null;

  return (
    (existingRules || []).find((rule) => {
      if (rule.id === (excludeId || form.id)) return false;
      if (rule.configurationStatus !== CONFIGURATION_STATUS.ACTIVE) return false;
      return scopesOverlap(candidate, rule, sources);
    }) || null
  );
}

function sourceIdsAvailable(form, sources = []) {
  const byId = new Map((sources || []).map((c) => [c.id, c]));
  const parentMap = subcategoryParentMap(sources);

  if (form.ruleScope === RULE_SCOPE.SELECTED_TRADE_CATEGORIES) {
    return (form.tradeCategoryIds || []).every((id) => byId.has(id));
  }

  if (form.ruleScope === RULE_SCOPE.SELECTED_SUBCATEGORIES) {
    const parentsOk = (form.parentTradeCategoryIds || []).every((id) => byId.has(id));
    const subsOk = (form.subcategoryIds || []).every((id) => parentMap.has(id));
    return parentsOk && subsOk;
  }

  return true;
}

export function collectKeywordRuleErrors(form, existingRules = [], sources = [], options = {}) {
  const { excludeId, checkOverlap = true } = options;
  const errors = {};

  const name = String(form.ruleName || '').trim();
  if (!name) errors.ruleName = 'Enter Rule Name.';
  else if (findDuplicateRuleName(existingRules, name, excludeId || form.id)) {
    errors.ruleName = 'This Rule Name is already in use.';
  }

  if (!form.applicationArea) errors.applicationArea = 'Select Application Area.';
  if (!form.ruleScope) errors.ruleScope = 'Select Rule Scope.';

  if (form.ruleScope === RULE_SCOPE.SELECTED_TRADE_CATEGORIES) {
    if (!(form.tradeCategoryIds || []).length) {
      errors.tradeCategoryIds = 'Select Trade Categories.';
    } else if (!sourceIdsAvailable(form, sources)) {
      errors.tradeCategoryIds = 'One or more selected Trade Categories are no longer available.';
    }
  }

  if (form.ruleScope === RULE_SCOPE.SELECTED_SUBCATEGORIES) {
    if (!(form.parentTradeCategoryIds || []).length) {
      errors.parentTradeCategoryIds = 'Select Parent Trade Categories.';
    }
    if (!(form.subcategoryIds || []).length) {
      errors.subcategoryIds = 'Select Subcategories.';
    } else if (!sourceIdsAvailable(form, sources)) {
      errors.subcategoryIds = 'One or more selected Subcategories are no longer available.';
    }
  }

  if (form.keywordUse !== KEYWORD_USE.ENABLED && form.keywordUse !== KEYWORD_USE.DISABLED) {
    errors.keywordUse = 'Select Keyword Use.';
  }

  if (
    form.configurationStatus !== CONFIGURATION_STATUS.ACTIVE &&
    form.configurationStatus !== CONFIGURATION_STATUS.INACTIVE
  ) {
    errors.configurationStatus = 'Select Configuration Status.';
  }

  if (
    checkOverlap &&
    !errors.applicationArea &&
    !errors.ruleScope &&
    !errors.tradeCategoryIds &&
    !errors.parentTradeCategoryIds &&
    !errors.subcategoryIds
  ) {
    const overlap = findActiveOverlap(form, existingRules, sources, excludeId);
    if (overlap) {
      errors._overlap = MESSAGES.overlap;
    }
  }

  return errors;
}

function snapshotComparable(rule) {
  return {
    ruleName: rule.ruleName || '',
    applicationArea: rule.applicationArea || '',
    ruleScope: rule.ruleScope || '',
    tradeCategoryIds: [...(rule.tradeCategoryIds || [])].sort(),
    parentTradeCategoryIds: [...(rule.parentTradeCategoryIds || [])].sort(),
    subcategoryIds: [...(rule.subcategoryIds || [])].sort(),
    keywordUse: rule.keywordUse || '',
    configurationStatus: rule.configurationStatus || '',
    ruleBehaviour: rule.ruleBehaviour || '',
  };
}

function diffValues(before, after) {
  const previousValues = {};
  const newValues = {};
  Object.keys(after).forEach((key) => {
    const prev = before[key];
    const next = after[key];
    const same =
      Array.isArray(prev) && Array.isArray(next)
        ? JSON.stringify(prev) === JSON.stringify(next)
        : prev === next;
    if (!same) {
      previousValues[key] = prev;
      newValues[key] = next;
    }
  });
  return { previousValues, newValues };
}

function prepareRulePayload(form, sources) {
  const cleaned = {
    ...form,
    ruleName: String(form.ruleName || '').trim(),
    tradeCategoryIds:
      form.ruleScope === RULE_SCOPE.SELECTED_TRADE_CATEGORIES
        ? [...(form.tradeCategoryIds || [])]
        : [],
    parentTradeCategoryIds:
      form.ruleScope === RULE_SCOPE.SELECTED_SUBCATEGORIES
        ? [...(form.parentTradeCategoryIds || [])]
        : [],
    subcategoryIds:
      form.ruleScope === RULE_SCOPE.SELECTED_SUBCATEGORIES
        ? [...(form.subcategoryIds || [])]
        : [],
  };

  return normalizeKeywordRule(
    {
      ...cleaned,
      ruleBehaviour: buildRuleBehaviourSummary(cleaned, sources),
      scopeSelectionDisplay: formatScopeSelectionDisplay(cleaned, sources),
    },
    sources,
  );
}

export function mergeRulesAfterCreate(existingRules, form, actor, sources = []) {
  const errors = collectKeywordRuleErrors(
    { ...form, configurationStatus: CONFIGURATION_STATUS.ACTIVE },
    existingRules,
    sources,
    { checkOverlap: true },
  );
  if (Object.keys(errors).length) {
    return {
      success: false,
      errors,
      message: errors._overlap || MESSAGES.failedApply,
      rules: existingRules,
    };
  }

  const now = nowIso();
  const actorName = actor?.name || 'System';
  const actorId = actor?.accountId || '—';
  const id = nextKeywordRuleId(existingRules);
  const preparedPreview = prepareRulePayload(
    { ...form, configurationStatus: CONFIGURATION_STATUS.ACTIVE },
    sources,
  );

  const createHistory = {
    action: 'Created',
    previousValues: null,
    newValues: snapshotComparable(preparedPreview),
    by: actorName,
    personId: actorId,
    on: now,
  };
  const payload = prepareRulePayload(
    {
      ...form,
      id,
      configurationStatus: CONFIGURATION_STATUS.ACTIVE,
      createdBy: actorName,
      createdOn: now,
      lastUpdatedBy: actorName,
      lastUpdatedOn: now,
      changeHistory: [createHistory],
    },
    sources,
  );
  emitKeywordCentralHistory(payload, createHistory);

  return {
    success: true,
    rule: payload,
    rules: [...existingRules, payload],
    message: MESSAGES.saved,
    errors: {},
  };
}

export function mergeRulesAfterUpdate(existingRules, form, actor, sources = []) {
  const existing = existingRules.find((r) => r.id === form.id);
  if (!existing) {
    return {
      success: false,
      errors: { _form: 'Rule not found.' },
      message: 'Rule not found.',
      rules: existingRules,
    };
  }

  const nextStatus = form.configurationStatus || existing.configurationStatus;
  const errors = collectKeywordRuleErrors(
    { ...form, configurationStatus: nextStatus },
    existingRules,
    sources,
    { excludeId: form.id, checkOverlap: true },
  );
  if (Object.keys(errors).length) {
    return {
      success: false,
      errors,
      message: errors._overlap || MESSAGES.failedApply,
      rules: existingRules,
    };
  }

  const now = nowIso();
  const actorName = actor?.name || 'System';
  const actorId = actor?.accountId || '—';
  const before = snapshotComparable(existing);
  const prepared = prepareRulePayload(
    {
      ...form,
      configurationStatus: nextStatus,
      createdBy: existing.createdBy,
      createdOn: existing.createdOn,
      lastUpdatedBy: actorName,
      lastUpdatedOn: now,
    },
    sources,
  );
  const after = snapshotComparable(prepared);
  const { previousValues, newValues } = diffValues(before, after);
  const historyEntry =
    Object.keys(newValues).length > 0
      ? {
          action: 'Updated',
          previousValues,
          newValues,
          by: actorName,
          personId: actorId,
          on: now,
        }
      : null;

  const updated = {
    ...prepared,
    changeHistory: historyEntry
      ? [...(existing.changeHistory || []), historyEntry]
      : [...(existing.changeHistory || [])],
  };
  if (historyEntry) emitKeywordCentralHistory(updated, historyEntry);

  return {
    success: true,
    rule: updated,
    rules: existingRules.map((r) => (r.id === updated.id ? updated : r)),
    message: MESSAGES.updated,
    errors: {},
  };
}

export function mergeRulesAfterMakeInactive(existingRules, ruleId, actor) {
  const existing = existingRules.find((r) => r.id === ruleId);
  if (!existing || existing.configurationStatus !== CONFIGURATION_STATUS.ACTIVE) {
    return { success: false, rules: existingRules, message: MESSAGES.failedApply };
  }

  const now = nowIso();
  const actorName = actor?.name || 'System';
  const actorId = actor?.accountId || '—';
  const inactiveHistory = {
    action: 'Made Inactive',
    previousValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
    newValues: { configurationStatus: CONFIGURATION_STATUS.INACTIVE },
    by: actorName,
    personId: actorId,
    on: now,
  };
  const updated = normalizeKeywordRule({
    ...existing,
    configurationStatus: CONFIGURATION_STATUS.INACTIVE,
    lastUpdatedBy: actorName,
    lastUpdatedOn: now,
    changeHistory: [...(existing.changeHistory || []), inactiveHistory],
  });
  emitKeywordCentralHistory(updated, inactiveHistory);

  return {
    success: true,
    rule: updated,
    rules: existingRules.map((r) => (r.id === ruleId ? updated : r)),
    message: MESSAGES.inactivated,
  };
}

export function mergeRulesAfterActivate(existingRules, ruleId, actor, sources = []) {
  const existing = existingRules.find((r) => r.id === ruleId);
  if (!existing || existing.configurationStatus !== CONFIGURATION_STATUS.INACTIVE) {
    return { success: false, rules: existingRules, message: MESSAGES.failedApply };
  }

  if (!sourceIdsAvailable(existing, sources)) {
    return {
      success: false,
      rules: existingRules,
      message: MESSAGES.failedApply,
      errors: { _form: 'One or more selected source records are no longer available.' },
    };
  }

  const candidate = {
    ...existing,
    configurationStatus: CONFIGURATION_STATUS.ACTIVE,
  };
  const overlap = findActiveOverlap(candidate, existingRules, sources, ruleId);
  if (overlap) {
    return {
      success: false,
      rules: existingRules,
      message: MESSAGES.overlap,
      errors: { _overlap: MESSAGES.overlap },
    };
  }

  const now = nowIso();
  const actorName = actor?.name || 'System';
  const actorId = actor?.accountId || '—';
  const activateHistory = {
    action: 'Activated',
    previousValues: { configurationStatus: existing.configurationStatus },
    newValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
    by: actorName,
    personId: actorId,
    on: now,
  };
  const updated = normalizeKeywordRule(
    {
      ...existing,
      configurationStatus: CONFIGURATION_STATUS.ACTIVE,
      lastUpdatedBy: actorName,
      lastUpdatedOn: now,
      changeHistory: [...(existing.changeHistory || []), activateHistory],
    },
    sources,
  );
  emitKeywordCentralHistory(updated, activateHistory);

  return {
    success: true,
    rule: updated,
    rules: existingRules.map((r) => (r.id === ruleId ? updated : r)),
    message: MESSAGES.activated,
  };
}
